import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useFilterHistory } from "../hooks/useFilterHistory";
import {
	clearOtherUsersPersistedViewState,
	readPersistedJson,
	type ViewStateScope,
	viewStateStorageKey,
	writePersistedJson,
} from "../utils/persistedViewState";

/**
 * View Mode Types
 */
export type ViewMode = "global" | "local" | "split";

export type GlobalViewType = "2d" | "list" | "duplicates";

export type LocalViewType = "detail" | "timeline" | "analytics";

/**
 * Split View Layout Configuration
 */
export type SplitLayout = "horizontal" | "vertical";

/**
 * Filter State
 */
export interface FilterState {
	searchQuery: string;
	nodeTypes: string[];
	dateRange?: {
		start: Date | null;
		end: Date | null;
	};
	customFilters: Record<string, unknown>;
}

/**
 * View State Interface
 */
export interface ViewState {
	// View Mode
	viewMode: ViewMode;
	setViewMode: (mode: ViewMode) => void;

	// Global View
	globalViewType: GlobalViewType;
	setGlobalViewType: (type: GlobalViewType) => void;

	// Local View
	localViewType: LocalViewType;
	setLocalViewType: (type: LocalViewType) => void;

	// Split View
	splitLayout: SplitLayout;
	setSplitLayout: (layout: SplitLayout) => void;

	// Focused Node
	focusedNodeId: string | null;
	setFocusedNodeId: (nodeId: string | null) => void;

	// Right-hand detail panel. `detailNodeId` is the node whose panel is open;
	// `connectionsListOpen` pushes the full connections browser on top of it,
	// and `focusedEdgeId` pushes the edge timeline on top of either (the panel
	// renders exactly one level at a time). None are persisted — an open panel
	// should not survive a reload, unlike `focusedNodeId`, which is also
	// URL-synced.
	detailNodeId: string | null;
	setDetailNodeId: (nodeId: string | null) => void;
	focusedEdgeId: string | null;
	setFocusedEdgeId: (edgeId: string | null) => void;
	connectionsListOpen: boolean;
	setConnectionsListOpen: (open: boolean) => void;
	/** Pops one panel level: edge timeline → connections browser → closed. */
	closeDetailPanel: () => void;
	/** Closes the panel outright, at whatever level it is on. */
	resetDetailPanel: () => void;
	/** Opens a node's panel, discarding any level pushed on top of it. */
	openNodePanel: (nodeId: string) => void;

	// Node Selection (supports multi-select)
	selectedNodeIds: string[];
	setSelectedNodeIds: (ids: string[]) => void;
	toggleNodeSelection: (id: string) => void;
	clearSelection: () => void;

	// Filters
	filters: FilterState;
	setFilters: (filters: Partial<FilterState>) => void;
	resetFilters: () => void;
	undoFilters: () => void;
	redoFilters: () => void;
	canUndoFilters: boolean;
	canRedoFilters: boolean;

	// UI State
	showLabels: boolean;
	setShowLabels: (show: boolean) => void;
	sidebarCollapsed: boolean;
	setSidebarCollapsed: (collapsed: boolean) => void;
	contextPanelVisible: boolean;
	setContextPanelVisible: (visible: boolean) => void;
	// Open accordion sections in the right-side context panel. Single persisted
	// slot shared across viewports — the Filters section is also force-opened
	// whenever filters are active, regardless of this value.
	contextPanelSections: string[];
	setContextPanelSections: (sections: string[]) => void;

	// Panel Sizes
	globalPanelSize: number;
	setGlobalPanelSize: (size: number) => void;
	localPanelSize: number;
	setLocalPanelSize: (size: number) => void;

	// Command Palette
	commandPaletteOpen: boolean;
	setCommandPaletteOpen: (open: boolean) => void;

	// Edit Request (for keyboard shortcut)
	editRequestPending: boolean;
	requestEditNode: () => void;
	clearEditRequest: () => void;

	// Mobile Drawer State (not persisted)
	mobileSidebarOpen: boolean;
	setMobileSidebarOpen: (open: boolean) => void;
	mobileContextPanelOpen: boolean;
	setMobileContextPanelOpen: (open: boolean) => void;

	// Fullscreen Animation
	fullscreenAnimationEnabled: boolean;
	setFullscreenAnimationEnabled: (enabled: boolean) => void;

	// FloatingLegend expansion. `null` means "no explicit user preference yet" —
	// the component falls back to a viewport default (desktop=open, mobile=closed).
	// When entity-type filters are active the legend force-opens regardless.
	legendExpanded: boolean | null;
	setLegendExpanded: (value: boolean | null) => void;
}

const defaultFilterState: FilterState = {
	searchQuery: "",
	nodeTypes: [],
	dateRange: {
		start: null,
		end: null,
	},
	customFilters: {},
};

const ViewStateContext = createContext<ViewState | undefined>(undefined);
const ViewStateScopeContext = createContext<ViewStateScope | null>(null);

interface ViewStateProviderProps {
	children: ReactNode;
	/**
	 * Whose view this is. Without a scope nothing is read or written, so a
	 * signed-out page cannot pick up the previous user's snapshot.
	 */
	scope?: ViewStateScope | null;
}

interface NormalizedViewState {
	viewMode: ViewMode;
	globalViewType: GlobalViewType;
	localViewType: LocalViewType;
	splitLayout: SplitLayout;
	focusedNodeId: string | null;
	selectedNodeIds: string[];
	filters: FilterState;
	showLabels: boolean;
	sidebarCollapsed: boolean;
	contextPanelVisible: boolean;
	contextPanelSections: string[];
	globalPanelSize: number;
	localPanelSize: number;
	fullscreenAnimationEnabled: boolean;
	legendExpanded: boolean | null;
}

function asRecord(value: unknown): Record<string, unknown> {
	if (value !== null && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return {};
}

function readViewMode(value: unknown): ViewMode {
	if (value === "global" || value === "local" || value === "split")
		return value;
	return "global";
}

function readGlobalViewType(value: unknown): GlobalViewType {
	// Silent migration: legacy "3d" preference falls back to "2d" since 3D was removed.
	if (value === "3d") return "2d";
	if (value === "2d" || value === "list" || value === "duplicates")
		return value;
	return "2d";
}

function readLocalViewType(value: unknown): LocalViewType {
	// Silent migration: legacy "kanban" preference falls back to "detail" since kanban was removed.
	if (value === "kanban") return "detail";
	if (value === "detail" || value === "timeline" || value === "analytics") {
		return value;
	}
	return "detail";
}

function readSplitLayout(value: unknown): SplitLayout {
	if (value === "horizontal" || value === "vertical") return value;
	return "horizontal";
}

function readBoolean(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

function readNumber(value: unknown, fallback: number): number {
	return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function readStringList(value: unknown, fallback: string[]): string[] {
	if (!Array.isArray(value)) return fallback;
	return value.filter((item): item is string => typeof item === "string");
}

function readFilters(value: unknown): FilterState {
	if (value === null || typeof value !== "object" || Array.isArray(value)) {
		return defaultFilterState;
	}
	return value as FilterState;
}

function normalizeStored(value: unknown): NormalizedViewState {
	const stored = asRecord(value);
	return {
		viewMode: readViewMode(stored.viewMode),
		globalViewType: readGlobalViewType(stored.globalViewType),
		localViewType: readLocalViewType(stored.localViewType),
		splitLayout: readSplitLayout(stored.splitLayout),
		focusedNodeId:
			typeof stored.focusedNodeId === "string" && stored.focusedNodeId
				? stored.focusedNodeId
				: null,
		selectedNodeIds: readStringList(stored.selectedNodeIds, []),
		filters: readFilters(stored.filters),
		showLabels: readBoolean(stored.showLabels, false),
		sidebarCollapsed: readBoolean(stored.sidebarCollapsed, false),
		contextPanelVisible: readBoolean(stored.contextPanelVisible, true),
		contextPanelSections: readStringList(stored.contextPanelSections, [
			"filters",
			"details",
		]),
		globalPanelSize: readNumber(stored.globalPanelSize, 60),
		localPanelSize: readNumber(stored.localPanelSize, 40),
		fullscreenAnimationEnabled: readBoolean(
			stored.fullscreenAnimationEnabled,
			true,
		),
		legendExpanded:
			typeof stored.legendExpanded === "boolean" ? stored.legendExpanded : null,
	};
}

function readScopedViewState(
	scope: ViewStateScope | null,
): NormalizedViewState {
	if (!scope) return normalizeStored(null);
	return normalizeStored(
		readPersistedJson(localStorage, viewStateStorageKey(scope)),
	);
}

export function ViewStateProvider({
	children,
	scope = null,
}: ViewStateProviderProps) {
	const scopeKey = scope ? viewStateStorageKey(scope) : null;
	const scopeRef = useRef(scope);
	scopeRef.current = scope;

	// Captured once. A later scope change is applied in the effect below so a
	// new user or organization never keeps the previous snapshot in memory.
	const initialRef = useRef<NormalizedViewState | null>(null);
	if (initialRef.current === null) {
		initialRef.current = readScopedViewState(scope);
	}
	const initial = initialRef.current;

	const [loadedScopeKey, setLoadedScopeKey] = useState<string | null>(scopeKey);

	// State
	const [viewMode, setViewMode] = useState<ViewMode>(initial.viewMode);
	const [globalViewType, setGlobalViewType] = useState<GlobalViewType>(
		initial.globalViewType,
	);
	const [localViewType, setLocalViewType] = useState<LocalViewType>(
		initial.localViewType,
	);
	const [splitLayout, setSplitLayout] = useState<SplitLayout>(
		initial.splitLayout,
	);
	const [focusedNodeId, setFocusedNodeId] = useState<string | null>(
		initial.focusedNodeId,
	);
	const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(
		initial.selectedNodeIds,
	);
	const [filters, setFiltersState] = useState<FilterState>(initial.filters);

	// Filter history for undo/redo
	const filterHistory = useFilterHistory(filters);

	// Track if we're applying history change to avoid adding to history
	const isApplyingHistoryRef = useRef(false);

	const [showLabels, setShowLabels] = useState<boolean>(initial.showLabels);
	const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
		initial.sidebarCollapsed,
	);
	const [contextPanelVisible, setContextPanelVisible] = useState<boolean>(
		initial.contextPanelVisible,
	);
	const [contextPanelSections, setContextPanelSections] = useState<string[]>(
		initial.contextPanelSections,
	);
	const [globalPanelSize, setGlobalPanelSize] = useState<number>(
		initial.globalPanelSize,
	);
	const [localPanelSize, setLocalPanelSize] = useState<number>(
		initial.localPanelSize,
	);

	const [commandPaletteOpen, setCommandPaletteOpen] = useState<boolean>(false);
	const [editRequestPending, setEditRequestPending] = useState<boolean>(false);

	// Detail panel targets (not persisted — see the interface comment)
	const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
	const [focusedEdgeId, setFocusedEdgeId] = useState<string | null>(null);
	const [connectionsListOpen, setConnectionsListOpen] = useState(false);

	// Mobile drawer state (not persisted to localStorage)
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);
	const [mobileContextPanelOpen, setMobileContextPanelOpen] =
		useState<boolean>(false);

	const [legendExpanded, setLegendExpanded] = useState<boolean | null>(
		initial.legendExpanded,
	);
	const [fullscreenAnimationEnabled, setFullscreenAnimationEnabled] =
		useState<boolean>(initial.fullscreenAnimationEnabled);

	const requestEditNode = useCallback(() => {
		setEditRequestPending(true);
	}, []);

	const clearEditRequest = useCallback(() => {
		setEditRequestPending(false);
	}, []);

	// Filter helpers
	const setFilters = useCallback(
		(newFilters: Partial<FilterState>) => {
			setFiltersState((prev) => {
				const newState = { ...prev, ...newFilters };

				// Add to history if not applying history change
				if (!isApplyingHistoryRef.current) {
					filterHistory.pushState(newState);
				}

				return newState;
			});
		},
		[filterHistory],
	);

	const resetFilters = useCallback(() => {
		setFiltersState(defaultFilterState);
		filterHistory.pushState(defaultFilterState);
	}, [filterHistory]);

	const undoFilters = useCallback(() => {
		const previousState = filterHistory.undo();
		if (previousState) {
			isApplyingHistoryRef.current = true;
			setFiltersState(previousState);
			isApplyingHistoryRef.current = false;
		}
	}, [filterHistory]);

	const redoFilters = useCallback(() => {
		const nextState = filterHistory.redo();
		if (nextState) {
			isApplyingHistoryRef.current = true;
			setFiltersState(nextState);
			isApplyingHistoryRef.current = false;
		}
	}, [filterHistory]);

	// Selection helpers
	const toggleNodeSelection = useCallback((id: string) => {
		setSelectedNodeIds((prev) => {
			if (prev.includes(id)) {
				return prev.filter((nodeId) => nodeId !== id);
			}
			return [...prev, id];
		});
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedNodeIds([]);
	}, []);

	const closeDetailPanel = useCallback(() => {
		if (focusedEdgeId !== null) {
			setFocusedEdgeId(null);
			return;
		}
		if (connectionsListOpen) {
			setConnectionsListOpen(false);
			return;
		}
		setDetailNodeId(null);
	}, [focusedEdgeId, connectionsListOpen]);

	const resetDetailPanel = useCallback(() => {
		setFocusedEdgeId(null);
		setConnectionsListOpen(false);
		setDetailNodeId(null);
	}, []);

	const openNodePanel = useCallback((nodeId: string) => {
		setFocusedEdgeId(null);
		setConnectionsListOpen(false);
		setDetailNodeId(nodeId);
	}, []);

	const { clearHistory } = filterHistory;
	const scrubbedUserIdRef = useRef<string | null>(null);

	// A different account on this browser must not be able to read what the
	// previous one saved, even when that session never reached sign-out.
	useEffect(() => {
		const userId = scope?.userId;
		if (!userId || scrubbedUserIdRef.current === userId) return;
		scrubbedUserIdRef.current = userId;
		clearOtherUsersPersistedViewState(localStorage, userId);
	}, [scope?.userId]);

	// Swap the in-memory screen when the user or organization changes. The
	// snapshot being left stays under its own key so returning restores it.
	useEffect(() => {
		if (loadedScopeKey === scopeKey) return;
		const next = scopeRef.current;
		const normalized = readScopedViewState(next);
		setViewMode(normalized.viewMode);
		setGlobalViewType(normalized.globalViewType);
		setLocalViewType(normalized.localViewType);
		setSplitLayout(normalized.splitLayout);
		setFocusedNodeId(normalized.focusedNodeId);
		setSelectedNodeIds(normalized.selectedNodeIds);
		setFiltersState(normalized.filters);
		clearHistory(normalized.filters);
		setShowLabels(normalized.showLabels);
		setSidebarCollapsed(normalized.sidebarCollapsed);
		setContextPanelVisible(normalized.contextPanelVisible);
		setContextPanelSections(normalized.contextPanelSections);
		setGlobalPanelSize(normalized.globalPanelSize);
		setLocalPanelSize(normalized.localPanelSize);
		setFullscreenAnimationEnabled(normalized.fullscreenAnimationEnabled);
		setLegendExpanded(normalized.legendExpanded);
		setDetailNodeId(null);
		setFocusedEdgeId(null);
		setConnectionsListOpen(false);
		setCommandPaletteOpen(false);
		setEditRequestPending(false);
		setMobileSidebarOpen(false);
		setMobileContextPanelOpen(false);
		setLoadedScopeKey(scopeKey);
	}, [clearHistory, loadedScopeKey, scopeKey]);

	// Persist only after the state on screen belongs to the current scope.
	// Writing earlier would save the previous screen under the new key.
	useEffect(() => {
		const current = scopeRef.current;
		if (!current || loadedScopeKey !== scopeKey) return;
		writePersistedJson(localStorage, viewStateStorageKey(current), {
			viewMode,
			globalViewType,
			localViewType,
			splitLayout,
			focusedNodeId,
			selectedNodeIds,
			filters,
			showLabels,
			sidebarCollapsed,
			contextPanelVisible,
			contextPanelSections,
			globalPanelSize,
			localPanelSize,
			fullscreenAnimationEnabled,
			legendExpanded,
		});
	}, [
		scopeKey,
		loadedScopeKey,
		viewMode,
		globalViewType,
		localViewType,
		splitLayout,
		focusedNodeId,
		selectedNodeIds,
		filters,
		showLabels,
		sidebarCollapsed,
		contextPanelVisible,
		contextPanelSections,
		globalPanelSize,
		localPanelSize,
		fullscreenAnimationEnabled,
		legendExpanded,
	]);

	const value: ViewState = {
		viewMode,
		setViewMode,
		globalViewType,
		setGlobalViewType,
		localViewType,
		setLocalViewType,
		splitLayout,
		setSplitLayout,
		focusedNodeId,
		setFocusedNodeId,
		detailNodeId,
		setDetailNodeId,
		focusedEdgeId,
		setFocusedEdgeId,
		connectionsListOpen,
		setConnectionsListOpen,
		closeDetailPanel,
		resetDetailPanel,
		openNodePanel,
		selectedNodeIds,
		setSelectedNodeIds,
		toggleNodeSelection,
		clearSelection,
		filters,
		setFilters,
		resetFilters,
		undoFilters,
		redoFilters,
		canUndoFilters: filterHistory.canUndo,
		canRedoFilters: filterHistory.canRedo,
		showLabels,
		setShowLabels,
		sidebarCollapsed,
		setSidebarCollapsed,
		contextPanelVisible,
		setContextPanelVisible,
		contextPanelSections,
		setContextPanelSections,
		globalPanelSize,
		setGlobalPanelSize,
		localPanelSize,
		setLocalPanelSize,
		commandPaletteOpen,
		setCommandPaletteOpen,
		editRequestPending,
		requestEditNode,
		clearEditRequest,
		mobileSidebarOpen,
		setMobileSidebarOpen,
		mobileContextPanelOpen,
		setMobileContextPanelOpen,
		fullscreenAnimationEnabled,
		setFullscreenAnimationEnabled,
		legendExpanded,
		setLegendExpanded,
	};

	return (
		<ViewStateScopeContext.Provider value={scope}>
			<ViewStateContext.Provider value={value}>
				{children}
			</ViewStateContext.Provider>
		</ViewStateScopeContext.Provider>
	);
}

export function useViewStateScope(): ViewStateScope | null {
	return useContext(ViewStateScopeContext);
}

export function useViewState() {
	const context = useContext(ViewStateContext);
	if (context === undefined) {
		throw new Error("useViewState must be used within a ViewStateProvider");
	}
	return context;
}

// Hook for optional use (won't throw if outside provider)
export function useViewStateOptional() {
	return useContext(ViewStateContext);
}
